import { apiClient } from '@/lib/api-client'

// Types
export interface PortalOrganizationInfo {
  slug: string
  name: string
  logo_url: string | null
}

export interface PortalEmployee {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone_number: string | null
  location: string | null
  seniority: string | null
  is_active: boolean
  onboarding_status: string | null
  invited_at: string | null
  created_at: string
}

export interface PortalEmployeeList {
  employees: PortalEmployee[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface PortalImportResult {
  row: number
  success: boolean
  user_id: number | null
  error: string | null
}

export interface PortalImportResponse {
  import_id: number
  total: number
  success_count: number
  failure_count: number
  results: PortalImportResult[]
  warnings: string[]
}

export interface PortalImportHistoryItem {
  id: number
  original_filename: string
  file_type: string
  total_rows: number
  success_count: number
  failure_count: number
  created_at: string
  uploaded_by_name: string | null
}

export interface PortalDashboardStats {
  total_employees: number
  active_employees: number
  total_imports: number
  recent_imports: PortalImportHistoryItem[]
}

export interface PortalEmployeeListParams {
  page?: number
  limit?: number
  search?: string
}

// API functions
export async function getOrgBranding(slug: string): Promise<PortalOrganizationInfo> {
  const response = await apiClient.get<PortalOrganizationInfo>(`/api/v1/portal/org/${slug}`)
  return response.data
}

export async function getPortalDashboard(): Promise<PortalDashboardStats> {
  const response = await apiClient.get<PortalDashboardStats>('/api/v1/portal/dashboard')
  return response.data
}

export async function getPortalEmployees(
  params: PortalEmployeeListParams = {},
): Promise<PortalEmployeeList> {
  const response = await apiClient.get<PortalEmployeeList>('/api/v1/portal/employees', { params })
  return response.data
}

export async function importPortalEmployees(file: File): Promise<PortalImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<PortalImportResponse>('/api/v1/portal/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export async function getPortalImportHistory(): Promise<PortalImportHistoryItem[]> {
  const response = await apiClient.get<PortalImportHistoryItem[]>('/api/v1/portal/imports')
  return response.data
}

export async function getPortalImportDownloadUrl(
  importId: number,
): Promise<{ download_url: string; filename: string }> {
  const response = await apiClient.get<{ download_url: string; filename: string }>(
    `/api/v1/portal/imports/${importId}/download`,
  )
  return response.data
}

export async function invitePortalEmployee(
  employeeId: number,
): Promise<{ message: string; employee_id: number }> {
  const response = await apiClient.post<{ message: string; employee_id: number }>(
    `/api/v1/portal/employees/${employeeId}/invite`,
  )
  return response.data
}

export async function downloadPortalImportTemplate(): Promise<void> {
  const response = await apiClient.get('/api/v1/portal/import/template', {
    responseType: 'blob',
  })
  const blob = new Blob([response.data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'employee_import_template.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
